import Phaser from 'phaser';

export type InputControllerOptions = Readonly<{
  scene: Phaser.Scene;
  onMove: (worldX: number) => void;
  onDrop: () => void;
  onPause: () => void;
  canControl: () => boolean;
}>;

export class InputController {
  private readonly scene: Phaser.Scene;
  private readonly onMove: (worldX: number) => void;
  private readonly onDrop: () => void;
  private readonly onPause: () => void;
  private readonly canControl: () => boolean;
  private keyboardX = 210;

  public constructor(options: InputControllerOptions) {
    this.scene = options.scene;
    this.onMove = options.onMove;
    this.onDrop = options.onDrop;
    this.onPause = options.onPause;
    this.canControl = options.canControl;
    this.bind();
  }

  public destroy(): void {
    this.scene.input.off('pointermove', this.handlePointerMove, this);
    this.scene.input.off('pointerup', this.handlePointerUp, this);
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
    window.removeEventListener('pointermove', this.handleWindowPointerMove);
  }

  public syncX(worldX: number): void {
    this.keyboardX = worldX;
  }

  private bind(): void {
    this.scene.input.on('pointermove', this.handlePointerMove, this);
    this.scene.input.on('pointerup', this.handlePointerUp, this);
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
    // Phaser reports pointer movement only while the cursor is over its canvas.
    // Horizontal placement remains useful above and below the square board, so
    // observe page-level movement too (but only across the canvas's X span).
    window.addEventListener('pointermove', this.handleWindowPointerMove);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.canControl()) return;
    const worldX = this.pointerWorldX(pointer);
    this.keyboardX = worldX;
    this.onMove(worldX);
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.canControl()) return;
    const worldX = this.pointerWorldX(pointer);
    this.keyboardX = worldX;
    this.onMove(worldX);
    this.onDrop();
  }

  private readonly handleWindowPointerMove = (event: PointerEvent): void => {
    if (!this.canControl() || !this.isWithinCanvasX(event.clientX)) return;
    const worldX = this.worldXFromClient(event.clientX);
    this.keyboardX = worldX;
    this.onMove(worldX);
  };

  private pointerWorldX(pointer: Phaser.Input.Pointer): number {
    const event = pointer.event;
    const source = typeof TouchEvent !== 'undefined' && event instanceof TouchEvent
      ? event.changedTouches[0]
      : event;
    if (source && 'clientX' in source && Number.isFinite(source.clientX)) {
      return this.worldXFromClient(source.clientX);
    }
    return (pointer.positionToCamera(this.scene.cameras.main) as Phaser.Math.Vector2).x;
  }

  private isWithinCanvasX(clientX: number): boolean {
    const bounds = this.scene.game.canvas.getBoundingClientRect();
    return clientX >= bounds.left && clientX <= bounds.right;
  }

  private worldXFromClient(clientX: number): number {
    // Responsive layout can move and resize the fitted canvas between Phaser
    // scale refreshes. Convert against its current DOM rectangle directly.
    const bounds = this.scene.game.canvas.getBoundingClientRect();
    const canvasX = (clientX - bounds.left) * (this.scene.scale.gameSize.width / bounds.width);
    return this.scene.cameras.main.getWorldPoint(canvasX, 0).x;
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented || (event.target instanceof HTMLElement && event.target.closest('a, button, input, select, textarea, [contenteditable]'))) {
      return;
    }
    if (event.key === 'Escape' || event.key.toLowerCase() === 'p') {
      event.preventDefault();
      this.onPause();
      return;
    }
    if (!this.canControl()) return;
    if (event.key === 'ArrowLeft' || event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.keyboardX -= 16;
      this.onMove(this.keyboardX);
    } else if (event.key === 'ArrowRight' || event.key.toLowerCase() === 'd') {
      event.preventDefault();
      this.keyboardX += 16;
      this.onMove(this.keyboardX);
    } else if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.onDrop();
    }
  }
}
